import React from "react";
import * as Yup from "yup";
import { connect } from "react-redux";
import * as interviewActions from "../../../actions/interviewActions";
import { setContinueAfterPiGate } from "../../../actions/interviewActions";
import { Field, Formik } from "formik";
import OnSubmitValidationError from "../../../components/FormComponents/OnSubmitValidationError";
import { WizardButtonsField } from "../Components/WizardButtons";
import * as formsService from "../../../services/formsService";
import { YesNoButtons } from "../../../components/FormComponents/YesNoButtons";
import { SimpleCheckboxGroup } from "@coverme/forms";

const validationSchema = Yup.object().shape({});

function getPatientPronoun(gender) {
    if (gender === "Male") {
        return "his";
    } else if (gender === "Female") {
        return "her";
    } else {
        return "their";
    }
}

const InterviewExchange = ({
    exchange,
    patientName,
    patientGender,
    onError,
    navigateToPreviousPage,
    onSubmit,
    navigateToNextPage,
}) => {
    function onSubmitInternal(formData) {
        return onSubmit(formData);
    }

    const patientPronoun = getPatientPronoun(patientGender);
    const lifeChangingEvents = [
        "Change in income",
        "Change in primary residence",
        "Death",
        "Denied Medicaid/CHIP",
        "Gained citizenship or lawful presence in the U.S.",
        "Gained or became a dependent",
        "Had a baby",
        "Released from incarceration (detention, jail, or prison)",
        "Was divorced or legally separated and lost health insurance",
        "Was married",        
        "None",
    ];
    return (
        <div className="wizard-page">
            <div className="title">
                <h2>Exchange</h2>
                <p className="sub-title" />
            </div>

            <Formik
                initialValues={exchange}
                onSubmit={onSubmitInternal}
                validationSchema={validationSchema}
                render={props => {
                    const { setFieldValue, setFieldTouched, resetForm, handleSubmit, errors, touched, values } = props;
                    const getValidationState = formsService.getValidationState.bind(this, errors, touched);
                    const getErrors = formsService.getErrors.bind(this, errors);
                    const isRequired = formsService.isRequired.bind(this, validationSchema, values);
                    return (
                        <form className="wizard-form vertical">
                            <OnSubmitValidationError callback={onError} />                            
                            <div className="form-section">
                                <div className="form-control">
                                    <YesNoButtons
                                        {...props}
                                        name={"hasRecentlyLostOrExpectingToLoseCoverage"}
                                        title={`Did ${patientName} or anyone in ${patientPronoun} household lose qualifying health coverage in the past 60 days or does ${patientName} expect anyone in ${patientPronoun} household to lose coverage in the next 60 days?`}
                                        setFieldValue={setFieldValue}
                                        getErrors={getErrors}
                                        isRequired={isRequired}
                                        getValidationState={getValidationState}
                                    />
                                </div>
                            </div>                            
                            <div className="form-section">
                                <div className="form-control">
                                    <Field
                                        name="lifeChangingEvents"
                                        render={({ field }) => (
                                            <SimpleCheckboxGroup
                                                title={`In the past 60 days did any of the following life events occur to ${patientName} or anyone in ${patientPronoun} household?`}
                                                flexDirection={"column"}
                                                changeEvent={(e, i) => {
                                                    let valueToSet = [...i];
                                                    // If None is the first item and another item has been selected, unselect None
                                                    if (i.indexOf("None") === 0 && i.length > 1) {
                                                        valueToSet = i.filter(x => x !== "None");
                                                    }

                                                    // If None has been selected after something else, remove other options.
                                                    if (i.indexOf("None") > 0) {
                                                        valueToSet = ["None"];
                                                    }
                                                    setFieldValue(field.name, valueToSet);
                                                }}
                                                selectedValues={field.value}
                                                groupName={"interview-exchange-lifeChangingEvents"}
                                                items={lifeChangingEvents}
                                                required={false}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="form-section">
                                <div className="form-control">
                                    <YesNoButtons
                                        {...props}
                                        name={"doesSmoke"}
                                        title={`Does ${patientName} smoke?`}
                                        setFieldValue={setFieldValue}
                                        getErrors={getErrors}
                                        isRequired={isRequired}
                                        getValidationState={getValidationState}
                                    />
                                </div>
                            </div>
                            <div className="form-section">
                                <div className="form-control">
                                    <YesNoButtons
                                        {...props}
                                        name={"didEmployerOfferHelpWithICHRA"}
                                        title={`Did ${patientName}'s employer offer to help with the cost of coverage through an ICHRA (Individual Coverage Health Reimbursement Arrangement)?`}
                                        setFieldValue={setFieldValue}
                                        getErrors={getErrors}
                                        isRequired={isRequired}
                                        getValidationState={getValidationState}
                                    />
                                </div>
                            </div>
                            <div className="form-section">
                                <div className="form-control">
                                    <YesNoButtons
                                        {...props}
                                        name={"didEmployerOfferHelpWithQSEHRA"}
                                        title={`Did ${patientName}'s employer offer to help with the cost of coverage through a QSEHRA (Qualified Small Employer Health Reimbursement Arrangement)?`}
                                        setFieldValue={setFieldValue}
                                        getErrors={getErrors}
                                        isRequired={isRequired}
                                        getValidationState={getValidationState}
                                    />
                                </div>
                            </div>
                            <div className="form-section">
                                <div className="form-control">
                                    <YesNoButtons
                                        {...props}
                                        name={"isMemberOfFederallyRecognizedTribe"}
                                        title={`Are you a member of a federally recognized tribe, or an Alaska Native corporation shareholder?`}
                                        setFieldValue={setFieldValue}
                                        getErrors={getErrors}
                                        isRequired={isRequired}
                                        getValidationState={getValidationState}
                                    />
                                </div>
                            </div>
                            <WizardButtonsField
                                shouldAutoNavigate={false}
                                navigateToNextPage={navigateToNextPage}
                                navigateToPreviousPage={navigateToPreviousPage}
                            />
                        </form>
                    );
                }}
            />
        </div>
    );
};

export const InterviewExchangeContainer = connect(
    state => ({
        exchange: state.interview.exchange,
        patientName: state.interview.entry.patient.fullName,
        patientGender: state.interview.entry.patient.gender,
    }),
    dispatch => ({
        setContinueAfterPiGate: shouldContinue => dispatch(setContinueAfterPiGate(shouldContinue)),
        onSubmit: entry => dispatch(interviewActions.setExchange(entry)),
        onError: () => dispatch(interviewActions.setInterviewError("pi")),
    })
)(InterviewExchange);
